import { DatePipe } from '@angular/common';
import { SelectionItem } from '../component/select/select.component';

// See rent/model/contract.ts for example usage.
export const PARENT_NAVIGATIONS = Symbol();

/** Type of all child-classes to ModelBase */
type ModelType = new (...args: any) => ModelBase;
type RecentType = ModelType | Function;
const RECENT_ID = Symbol();

export abstract class ModelBase {
  constructor() {}

  id: string;
  get isNew(): boolean {
    return !this.id || this.id.length === 0 || /^\s*$/.test(this.id);
  }

  private static RecentType: RecentType;
  private static Navigation: RecentType[] = [];

  /**
   * Store a recent id statically on the Runtime-type of this model instance.
   * The form-component.base and list-view classes call this automatically.
   */
  public storeRecentId({
    isDeactivation = false,
  }: {
    /** Should be true if this call originates from a canDeactivate block. */
    isDeactivation?: boolean;
  } = {}): void {
    try {
      const name = this.constructor.name; // <-- (garbage after minification, only helpful in local debugging)
      const idOld = this.constructor[RECENT_ID];
      const idNew = this.id;

      // When a supported navigation is taking place, store-calls from component deactivation must be ignored.
      // This is because deactivation occurs AFTER the store-call from the navigated-to item has been made.
      if (isDeactivation && idNew === idOld && ModelBase.Navigation?.includes(this.constructor)) {
        console.debug('Navigation in progress. Ignoring id store for:', name);
        return;
      }

      // Store the new id:
      console.debug(`Updating stored id for ${name} (from, to):`, idOld, idNew);
      this.constructor[RECENT_ID] = idNew;

      // Check for explicitly supported navigations (if any has been declared):
      let supportedDeepNavigations = this.constructor[PARENT_NAVIGATIONS] as RecentType[];
      supportedDeepNavigations = Array.isArray(supportedDeepNavigations) ? supportedDeepNavigations : [];

      // If parent type is also a valid model then implicitly treat it as deep-navigatable.
      const parentType = Object.getPrototypeOf(this.constructor.prototype).constructor;
      if (parentType !== ModelBase) {
        supportedDeepNavigations.push(parentType); // e.g. for the various Contract classes.

        const parentNav = parentType[PARENT_NAVIGATIONS] as RecentType[];
        if (Array.isArray(parentNav)) {
          supportedDeepNavigations.push(...parentNav); // inherit navigations from parent.
        }
      }

      // If the existing RecentType is a supported navigation of the new type,
      // then shift the existing RecentType into our navigation history. (Ordered!)
      if (supportedDeepNavigations.includes(ModelBase.RecentType)) {
        console.debug('Is navigation from:', ModelBase.RecentType.name);
        ModelBase.Navigation = [ModelBase.RecentType, ...ModelBase.Navigation];
      } else {
        ModelBase.Navigation = []; // ...otherwise clear the navigation history.
      }

      // Lastly set RecentType to new type:
      ModelBase.RecentType = this.constructor;
    } catch (err: unknown) {
      console.debug('Failed to store id for:', this?.constructor?.name, this, err);
    }
  }
  /**
   * Consume a recent id stored statically on the Runtime-type of this model instance.
   * List-view components use this method for highlighting of the most recently viewed item.
   */
  public consumeRecentId({
    ignoreRecentType = false,
  }: {
    /**
     * False: Consume the most recently stored id ONLY if the this type matches the most recently stored type, or is part of its type-chain.
     * True: Consume the most recently stored id of this type regardless of what the most recently stored type is.
     */
    ignoreRecentType?: boolean;
  } = {}): string | undefined {
    const isRecent = ModelBase.RecentType === this.constructor;
    let chainIndex = ModelBase.Navigation?.indexOf(this.constructor);
    if (ignoreRecentType || isRecent || chainIndex !== -1) {
      try {
        const name = this.constructor.name; // <-- (garbage after minification, only helpful in local debugging)
        const id = this.constructor[RECENT_ID];
        this.constructor[RECENT_ID] = undefined;
        console.debug(`Consumed stored id for ${name}:`, id);

        if (isRecent || chainIndex !== -1) {
          const parentIndex = chainIndex + 1;
          const parent = ModelBase.Navigation.length > parentIndex ? ModelBase.Navigation[parentIndex] : undefined;
          ModelBase.RecentType = parent;

          if (parent) {
            ModelBase.Navigation.splice(0, parentIndex + 1);
            console.debug('Popped navigation for:', parent.name);
          } else {
            ModelBase.Navigation = [];
            console.debug('Cleared navigation chain.');
          }
        }

        return id;
      } catch (err: unknown) {
        console.debug('Failed to consume id for:', this?.constructor?.name, this, err);
      }
    }
    console.debug('Recent id ignored as RecentType is not:', this?.constructor?.name);
    return undefined;
  }
  /**
   * Consume a recent id stored statically on this model type.
   * This method always ignores RecentType value.
   */
  public static ConsumeRecentId<TModel extends ModelType>(this: TModel): string | undefined {
    // ^The above syntax adds this static method to all child-classes! --Nick
    try {
      const id = this[RECENT_ID];
      this[RECENT_ID] = undefined;
      console.debug('Consumed stored id:', id);
      return id;
    } catch {
      return undefined;
    }
  }
  // Hint: The three methods above stores data as static variables on the runtime-type child classes.
  // For example you can do this:
  // contact.id = "hello"; // <---- any instance of Contact
  // contact.storeRecentId();
  // dwellingContract.id = "world"; // <---- any instance of DwellingContract
  // dwellingContract.storeRecentId();
  // console.log(Contact.ConsumeRecentId()); // <---- logs "hello" (static method call on the class!)
  // console.log(DwellingContract.ConsumeRecentId()); // <---- logs "world" (static method call on the class!)

  protected clearTimezoneOffset(date: Date): Date {
    if (!date) return null;

    date = this.coerceDate(date);
    const offset = date.getTimezoneOffset() / 60;
    date.setHours(date.getHours() - offset);
    return date;
  }

  protected coerceDate(date: string | Date) {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    return date;
  }

  protected dateToString(date: Date, locale?: string): string {
    const pipe = new DatePipe(locale);
    return pipe.transform(date, 'shortDate');
  }

  round(value: number, decimalPlaces: number): number {
    return Number(Math.round(Number(`${value}e${decimalPlaces}`)) + `e-${decimalPlaces}`);
  }

  toAutocompleteValue(consumerType?: 'list' | 'field', culture?: string): string {
    return this.toString(culture);
  }

  toString(culture?: string): string {
    return '';
  }

  toSelectionItem(): SelectionItem<string> {
    return new SelectionItem(this.toString(), this.id);
  }
}

export enum ModelState {
  NotModified = 0,
  New = 1,
  Modified = 2,
  Deleted = 3,
}
